// src/components/seo/HeadSEO.tsx

import { Helmet } from 'react-helmet-async';

type HeadSEOProps = {
  title?: string;
  description?: string;
  canonical?: string;
  ogImage?: string;
  ogImageAlt?: string;
  noIndex?: boolean;
};

const HeadSEO = ({
  title = 'Bookoll — Software de reservas online y gestión de agenda',
  description = 'Bookoll es un software de reservas online para negocios de servicios. Gestiona citas, agenda, clientes y trabajadores desde un único panel.',
  canonical = 'https://bookoll.com/',
  ogImage = 'https://bookoll.com/og-home.jpg',
  ogImageAlt,
  noIndex = false,
}: HeadSEOProps) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Bookoll',
    url: 'https://bookoll.com',
    applicationCategory: 'BusinessApplication',
    description,
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'EUR',
    },
  };

  const imageAlt = ogImageAlt ?? title;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta
        name="robots"
        content={
          noIndex
            ? 'noindex, nofollow'
            : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1'
        }
      />
      <link rel="canonical" href={canonical} />
      {/* [12] Solo x-default — sin hreflang duplicado */}
      {!noIndex && (
        <link rel="alternate" hrefLang="x-default" href={canonical} />
      )}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Bookoll" />
      <meta property="og:locale" content="es_ES" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={imageAlt} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@bookoll" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={imageAlt} />

      {/* JSON-LD */}
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

export default HeadSEO;
